import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { storableError } from '../util/errors';
import { LISTING_STATE_PUBLISHED } from '../util/types';

// Profile-photo reminder (D46). The wizard's publish thunk is caught by its action type, so the
// EditListingPage duck stays untouched; ui.duck.test.js checks the string against the thunk.
export const PUBLISH_LISTING_FULFILLED = 'EditListingPage/publishListing/fulfilled';
const TASKS_LISTING_TYPE = 'tasks';

// A service = any listing type except tasks (the D40 home feed rule); drafts and listings pending
// approval are not published
const isPublishedService = listing => {
  const { state, publicData } = listing?.attributes || {};
  const listingType = publicData?.listingType;
  return state === LISTING_STATE_PUBLISHED && !!listingType && listingType !== TASKS_LISTING_TYPE;
};

// ================ Async thunks ================ //

// Does the user have a published service? One GET of their published listings (two fields), asked
// only for a user the signup reminder may concern. The user entity's currentUserHasListings is no
// substitute: it counts tasks too.
const fetchHasPublishedServicePayloadCreator = (userId, { extra: sdk, rejectWithValue }) => {
  const params = {
    states: LISTING_STATE_PUBLISHED,
    perPage: 100,
    'fields.listing': ['state', 'publicData.listingType'],
  };
  return sdk.ownListings
    .query(params)
    .then(response => ({
      userId,
      hasPublishedService: response.data.data.some(isPublishedService),
    }))
    .catch(e => rejectWithValue(storableError(e)));
};

export const fetchHasPublishedServiceThunk = createAsyncThunk(
  'ui/fetchHasPublishedService',
  fetchHasPublishedServicePayloadCreator,
  {
    // Once per user and session: no repeat while in progress, after an answer or after an error
    condition: (userId, { getState }) => getState().ui.publishedServiceCheck.userId !== userId,
  }
);

// ================ Slice ================ //

const initialState = {
  disableScrollRequests: [],
  // A service was published in this session; closing the photo reminder clears it
  photoReminderPublishPending: false,
  // A listing of any type was published in this session (D51): the email-verification reminder
  // then shows regardless of its 72-hour stamp; closing it clears this
  emailReminderPublishPending: false,
  publishedServiceCheck: { userId: null, inProgress: false, hasPublishedService: null },
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    disableScrolling: (state, action) => {
      const { componentId, disableScrolling } = action.payload;
      const disableScrollRequests = state.disableScrollRequests;
      const componentIdExists = disableScrollRequests.find(c => c.componentId === componentId);

      if (componentIdExists) {
        const disableScrollRequestArray = disableScrollRequests.map(c => {
          return c.componentId === componentId ? { ...c, disableScrolling } : c;
        });
        state.disableScrollRequests = [...disableScrollRequestArray];
      } else {
        state.disableScrollRequests = [...disableScrollRequests, { componentId, disableScrolling }];
      }
    },
    clearPhotoReminderPublishPending: state => {
      state.photoReminderPublishPending = false;
    },
    clearEmailReminderPublishPending: state => {
      state.emailReminderPublishPending = false;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchHasPublishedServiceThunk.pending, (state, action) => {
        state.publishedServiceCheck = {
          userId: action.meta.arg,
          inProgress: true,
          hasPublishedService: null,
        };
      })
      .addCase(fetchHasPublishedServiceThunk.fulfilled, (state, action) => {
        const check = state.publishedServiceCheck;
        check.inProgress = false;
        // A publish seen while the query was on its way wins over its answer
        check.hasPublishedService =
          check.hasPublishedService === true || action.payload.hasPublishedService;
      })
      .addCase(fetchHasPublishedServiceThunk.rejected, state => {
        // Unknown stays null: no signup reminder rather than a wrong one
        state.publishedServiceCheck.inProgress = false;
      })
      .addCase(PUBLISH_LISTING_FULFILLED, (state, action) => {
        const listing = action.payload?.data?.data;
        if (listing?.attributes?.state === LISTING_STATE_PUBLISHED) {
          state.emailReminderPublishPending = true;
        }
        if (isPublishedService(listing)) {
          state.photoReminderPublishPending = true;
          state.publishedServiceCheck.hasPublishedService = true;
        }
      });
  },
});

// ================ Exports ================ //

export const {
  disableScrolling,
  clearPhotoReminderPublishPending,
  clearEmailReminderPublishPending,
} = uiSlice.actions;
export default uiSlice.reducer;

// ================ Helper function ================ //

export const manageDisableScrolling = (componentId, shouldDisableScrolling) =>
  disableScrolling({ componentId, disableScrolling: shouldDisableScrolling });

// ================ Selectors ================ //

export const isScrollingDisabled = state => {
  const { disableScrollRequests } = state.ui;
  return disableScrollRequests.some(r => r.disableScrolling);
};
